// Shared speech-audio bus. The avatar reads a single 0..1 "mouth level" each
// animation frame to lip-sync with whatever voice is currently speaking.
//
// Two sources feed it:
//  • ElevenLabs / cloud voice → a real <audio> element is routed through a Web
//    Audio AnalyserNode; the level is the live RMS amplitude of the waveform.
//  • System voice (SpeechSynthesis) → the browser exposes no audio data, so we
//    drive a believable synthetic talking envelope while it speaks.
//
// Everything is module-global and lazily initialized so multiple avatars on the
// page share one analysis pass.

let ctx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let data: Uint8Array | null = null;
let srcEl: HTMLMediaElement | null = null;
let srcNode: MediaElementAudioSourceNode | null = null;
let synthUntil = 0; // performance.now() until which the synthetic envelope runs
let level = 0; // smoothed 0..1 mouth opening
let lastUpd = 0;

function audioCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  } catch { ctx = null; }
  return ctx;
}

/**
 * Route a freshly-created <audio> element through the analyser so the avatar can
 * lip-sync to it. Must be a NEW element each time — a MediaElementSource can only
 * be created once per element. The element stays audible (analyser → destination).
 */
export function attachSpeechAudio(el: HTMLAudioElement): void {
  const a = audioCtx();
  if (!a) return;
  try {
    if (a.state === 'suspended') void a.resume();
    if (!analyser) {
      analyser = a.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.55;
      data = new Uint8Array(analyser.frequencyBinCount);
    }
    if (srcNode) { try { srcNode.disconnect(); } catch { /* ignore */ } srcNode = null; }
    srcNode = a.createMediaElementSource(el);
    srcNode.connect(analyser);
    analyser.connect(a.destination);
    srcEl = el;
  } catch {
    // Routing failed (e.g. CORS-tainted) — fall back to a synthetic envelope so
    // the mouth still moves while audio plays.
    srcEl = null;
    startSyntheticSpeech();
  }
}

// ── Persistent, gesture-unlocked playback ────────────────────────────────────
// Cloud synthesis can take 6–11 s; by the time the audio is ready the user
// gesture that started it has expired, so a fresh `new Audio().play()` is
// blocked by the browser (especially iOS/Safari) → the voice silently falls
// back to the system one. The fix: keep ONE persistent <audio> element, unlock
// it on the first user gesture (play a silent clip + resume the AudioContext),
// and reuse it. After the unlock, output flows through the running AudioContext,
// so delayed programmatic playback is allowed.

let persistentEl: HTMLAudioElement | null = null;
let persistentWired = false;
let unlocked = false;
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

function ensurePersistentEl(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  if (!persistentEl) {
    persistentEl = new Audio();
    persistentEl.preload = 'auto';
  }
  if (!persistentWired) {
    // Route the persistent element through the analyser exactly once.
    attachSpeechAudio(persistentEl);
    persistentWired = srcEl === persistentEl; // false if routing threw (CORS, etc.)
  }
  return persistentEl;
}

/** Call inside a user gesture to authorize later programmatic playback. */
export function unlockAudio(): void {
  if (unlocked) return;
  const a = audioCtx();
  if (a && a.state === 'suspended') void a.resume();
  const el = ensurePersistentEl();
  if (!el) return;
  try {
    el.muted = true;
    el.src = SILENT_WAV;
    const p = el.play();
    if (p && typeof p.then === 'function') {
      p.then(() => { el.pause(); el.muted = false; unlocked = true; })
       .catch(() => { el.muted = false; /* stays locked; try again next gesture */ });
    } else {
      el.pause(); el.muted = false; unlocked = true;
    }
  } catch { el.muted = false; }
}

/**
 * Play a synthesized clip through the persistent, unlocked element. Returns the
 * element (already routed for lip-sync), or null if playback couldn't start —
 * the caller then falls back to the system voice.
 */
export async function playSpeechUrl(
  url: string,
  handlers: { onPlay?: () => void; onEnd?: () => void },
): Promise<HTMLAudioElement | null> {
  const a = audioCtx();
  if (a && a.state === 'suspended') { try { await a.resume(); } catch { /* ignore */ } }
  const el = ensurePersistentEl();
  if (!el) return null;
  el.onplay = () => handlers.onPlay?.();
  el.onended = () => handlers.onEnd?.();
  el.onerror = () => handlers.onEnd?.();
  el.muted = false;
  el.src = url;
  try {
    await el.play();
    unlocked = true;
    return el;
  } catch {
    return null; // autoplay still blocked → caller falls back
  }
}

/** Stop the persistent element (used when the user interrupts). */
export function stopSpeechUrl(): void {
  if (persistentEl) { try { persistentEl.pause(); } catch { /* ignore */ } persistentEl.removeAttribute('src'); }
}

/** Drive the synthetic talking envelope (system voice path) for up to `ms`. */
export function startSyntheticSpeech(ms = 60_000): void {
  synthUntil = perfNow() + ms;
}
export function stopSyntheticSpeech(): void {
  synthUntil = 0;
}

function perfNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}

// Self-install: unlock audio on the very first user interaction anywhere, so the
// twin's cloned voice can play even when synthesis finishes seconds later.
if (typeof window !== 'undefined') {
  const onFirstGesture = () => { unlockAudio(); };
  for (const ev of ['pointerdown', 'keydown', 'touchstart'] as const) {
    window.addEventListener(ev, onFirstGesture, { capture: true, passive: true });
  }
}

/**
 * Current mouth-open level, 0..1. Smoothed and rate-limited so it can be called
 * by several avatars every frame without over-smoothing.
 */
export function speechLevel(): number {
  const now = perfNow();
  if (now - lastUpd >= 8) {
    let target = 0;
    if (analyser && data && srcEl && !srcEl.paused && !srcEl.ended) {
      analyser.getByteTimeDomainData(data as Uint8Array<ArrayBuffer>);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i]! - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      target = Math.min(1, rms * 3.4);
    } else if (now < synthUntil) {
      const t = now / 1000;
      // Two detuned oscillators → an organic open/close cadence with pauses.
      const e = (Math.sin(t * 11.3) * 0.5 + 0.5) * (Math.sin(t * 3.1 + 1.7) * 0.5 + 0.5);
      target = 0.12 + e * 0.8;
    }
    const ease = now - lastUpd > 200 ? 1 : 0.35; // snap if we were idle a while
    level += (target - level) * ease;
    lastUpd = now;
  }
  return level;
}
